import { useState, useEffect, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from './ToastProvider';
import Button from './Button';
import BasicInput from './BasicInput';
import Select from './Select';
import WithdrawDetailsModal from './WithdrawDetailsModal';
import { getWithdrawalPreview, submitWithdrawal } from '@/api/finance';
import { formatCurrency } from '@/utils/formatUtils';
import { WITHDRAW_BANK_INFO_STORAGE_KEY } from '@/config/storageKeys';
import closeIcon from '@/assets/close.svg';

const WithdrawModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Save bank info to localStorage
  const saveBankInfoToStorage = (bankInfo) => {
    try {
      localStorage.setItem(WITHDRAW_BANK_INFO_STORAGE_KEY, JSON.stringify(bankInfo));
    } catch (error) {
      console.error('Error saving bank info to localStorage:', error);
    }
  };

  // Load bank info from localStorage
  const loadBankInfoFromStorage = () => {
    try {
      const savedBankInfo = localStorage.getItem(WITHDRAW_BANK_INFO_STORAGE_KEY);
      return savedBankInfo ? JSON.parse(savedBankInfo) : null;
    } catch (error) {
      console.error('Error loading bank info from localStorage:', error);
      return null;
    }
  };
  
  // Preview state
  const [previewData, setPreviewData] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewToken, setPreviewToken] = useState(null);

  const [isAnimatingIn, setIsAnimatingIn] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [withdrawalResult, setWithdrawalResult] = useState(null);
  const panelRef = useRef(null);
  const withdrawableAmount = previewData?.amount || 0;
  const finalAmount = previewData?.payout || 0;
  const [isVisible, setIsVisible] = useState(false);

  // Get withdrawal preview once when modal opens
  useEffect(() => {
    if (isOpen) {
      const fetchInitialPreview = async () => {
        setIsLoadingPreview(true);
        try {
          // Call preview API without bank details to get withdrawable amount and fees
          const response = await getWithdrawalPreview({});
          setPreviewData(response.data);
        } catch (error) {
          console.error('Error fetching initial withdrawal preview:', error);
          setPreviewData(null);
        } finally {
          setIsLoadingPreview(false);
        }
      };

      fetchInitialPreview();
    } else {
      // Reset preview data when modal closes
      setPreviewData(null);
      setPreviewToken(null);
      setIsLoadingPreview(false);
    }
  }, [isOpen]);

  // Form state with initial data from localStorage
  const [formData, setFormData] = useState(() => {
    const savedBankInfo = loadBankInfoFromStorage();
    return savedBankInfo || {
      bankName: '',
      branchName: '',
      accountType: '',
      accountNumber: '',
      accountHolder: '',
      accountHolderKana: ''
    };
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Account type options
  const accountTypeOptions = [
    { value: 'REGULAR', label: t('withdraw.accountTypes.regular') },
    { value: 'SAVINGS', label: t('withdraw.accountTypes.savings') },
    { value: 'CHECKING', label: t('withdraw.accountTypes.checking') }
  ];

  // Animation effects
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Force a reflow to ensure the initial state is applied
      if (panelRef.current) {
        panelRef.current.offsetHeight;
      }
      // Use requestAnimationFrame to ensure the animation starts after the element is rendered
      requestAnimationFrame(() => {
        setIsAnimatingIn(true);
      });
    } else {
      setIsAnimatingIn(false);
      setTimeout(() => setIsVisible(false), 300);
    }
  }, [isOpen]);

  // Reset form when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        bankName: '',
        branchName: '',
        accountType: '',
        accountNumber: '',
        accountHolder: '',
        accountHolderKana: ''
      });
      setErrors({});
      setIsSubmitting(false);
    }
  }, [isOpen]);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    if (!formData.bankName.trim()) {
      newErrors.bankName = t('withdraw.errors.bankName_required');
      isValid = false;
    }

    if (!formData.branchName.trim()) {
      newErrors.branchName = t('withdraw.errors.branchName_required');
      isValid = false;
    }

    if (!formData.accountType) {
      newErrors.accountType = t('withdraw.errors.accountType_required');
      isValid = false;
    }

    if (!formData.accountNumber.trim()) {
      newErrors.accountNumber = t('withdraw.errors.accountNumber_required');
      isValid = false;
    }

    if (!formData.accountHolderKana.trim()) {
      newErrors.accountHolderKana = t('withdraw.errors.accountHolderKana_required');
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    // Get token for submission
    let tokenToUse = previewToken;
    if (!tokenToUse) {
      try {
        setIsLoadingPreview(true);
        const previewResponse = await getWithdrawalPreview({
          bankName: formData.bankName,
          branchName: formData.branchName,
          accountType: formData.accountType,
          accountNumber: formData.accountNumber,
          accountHolder: formData.accountHolderKana,
          accountHolderKana: formData.accountHolderKana
        });
        tokenToUse = previewResponse.data.token;
        setPreviewToken(tokenToUse);
        setIsLoadingPreview(false);
      } catch (error) {
        console.error('Error getting withdrawal preview:', error);
        toast(t('withdraw.errors.preview_failed'));
        setIsLoadingPreview(false);
        return;
      }
    }

    setIsSubmitting(true);
    try {
      // Submit withdrawal with token
      const withdrawalResponse = await submitWithdrawal({
        bankName: formData.bankName,
        branchName: formData.branchName,
        accountType: formData.accountType,
        accountNumber: formData.accountNumber,
        accountHolder: formData.accountHolderKana,
        accountHolderKana: formData.accountHolderKana
      }, tokenToUse);

      // Invalidate finance activities to refresh the list
      queryClient.invalidateQueries(['financeActivities']);
      queryClient.invalidateQueries(['walletBalance']);

      // Save bank info to localStorage after successful submission
      saveBankInfoToStorage({
        bankName: formData.bankName,
        branchName: formData.branchName,
        accountType: formData.accountType,
        accountNumber: formData.accountNumber,
        accountHolder: formData.accountHolder,
        accountHolderKana: formData.accountHolderKana
      });

      // Show withdrawal details
      setWithdrawalResult(withdrawalResponse.data);
      setIsDetailsModalOpen(true);
      // onClose(); // Close the withdraw form modal

    } catch (error) {
      console.error('Error submitting withdrawal:', error);
      toast(t('withdraw.errors.submission_failed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.bankName && formData.branchName && formData.accountType &&
                     formData.accountNumber && formData.accountHolderKana;

  const canWithdraw = finalAmount > 0;

  if (!isVisible) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 flex flex-col justify-center items-end !border-t-0 z-50">
        <div 
          ref={panelRef}
          className={`w-[480px] h-full bg-white flex flex-col relative transition-transform duration-300 ease-out ${
            isAnimatingIn ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          {/* Header */}
          <div className="w-full h-[72px] py-6 px-6 relative flex justify-start items-center gap-2.5">
            <div className="flex-1 py-6 flex justify-start items-center">
              <div className="flex justify-start items-center gap-2">
                <div className="text-[#111111] text-lg font-bold leading-[27px]">
                  {t('withdraw.title')}
                </div>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="w-6 h-6 absolute right-6 top-6 flex items-center justify-center"
            >
              <img src={closeIcon} alt="close" className="w-7 h-7" />
            </button>
          </div>

          {/* Form Content */}
          <div className="flex-1 px-6 pb-[180px] bg-white rounded-md flex flex-col justify-start items-start overflow-hidden">
            <div className="self-stretch flex-1 pb-32 bg-white rounded-md flex flex-col justify-start items-start overflow-y-auto">
              <div className="self-stretch flex-1 flex flex-col justify-start items-end gap-6">
                <div className="self-stretch flex flex-col justify-start items-start gap-4">
                  <div className="self-stretch flex flex-col justify-start items-start gap-3">
                    {/* Bank Name Input */}
                    <BasicInput
                      title={t('withdraw.bankName')}
                      value={formData.bankName}
                      onChange={(e) => handleInputChange('bankName', e.target.value)}
                      placeholder={t('withdraw.bankNamePlaceholder')}
                      error={errors.bankName}
                      containerClassName="w-full"
                    />

                    {/* Branch Name Input */}
                    <BasicInput
                      title={t('withdraw.branchName')}
                      value={formData.branchName}
                      onChange={(e) => handleInputChange('branchName', e.target.value)}
                      placeholder={t('withdraw.branchNamePlaceholder')}
                      error={errors.branchName}
                      containerClassName="w-full"
                    />

                    {/* Account Type Select */}
                    <div className="self-stretch flex flex-col justify-start items-start gap-1">
                      <div className="self-stretch inline-flex justify-start items-start gap-3">
                        <div className="flex-1 flex justify-start items-center gap-1">
                          <div className="text-[#111111] text-sm">
                            {t('withdraw.accountType')}
                          </div>
                        </div>
                      </div>
                      <Select
                        options={accountTypeOptions}
                        value={formData.accountType}
                        onValueChange={(value) => handleInputChange('accountType', value)}
                        placeholder={t('withdraw.accountTypePlaceholder')}
                        className="w-full h-12 text-start !rounded-md bg-[#f7f7f7]"
                      />
                      {errors.accountType && (
                        <div className="text-[#ff5501] text-xs leading-[18px]">
                          {errors.accountType}
                        </div>
                      )}
                    </div>

                    {/* Account Number Input */}
                    <BasicInput
                      title={t('withdraw.accountNumber')}
                      value={formData.accountNumber}
                      onChange={(e) => handleInputChange('accountNumber', e.target.value)}
                      placeholder={t('withdraw.accountNumberPlaceholder')}
                      error={errors.accountNumber}
                      containerClassName="w-full"
                    />

                    {/* Account Holder Kana Input */}
                    <BasicInput
                      title={t('withdraw.accountHolderKana')}
                      value={formData.accountHolderKana}
                      onChange={(e) => handleInputChange('accountHolderKana', e.target.value)}
                      placeholder={t('withdraw.accountHolderKanaPlaceholder')}
                      error={errors.accountHolderKana}
                      containerClassName="w-full"
                    />
                  </div>
                </div>

                {/* Notice Section */}
                <div className="self-stretch p-5 bg-[#f7f7f7] rounded-md outline outline-1 outline-offset-[-1px] outline-[#eeeeee] flex flex-col justify-center items-center gap-4">
                  <div className="flex flex-col justify-start items-center gap-2">
                    <div className="w-[392px] text-[#666666] text-xs leading-[18px]">
                      {t('withdraw.feeNotice')}
                    </div>
                    <div className="w-[392px] text-[#666666] text-xs leading-[18px]">
                      {t('withdraw.reversalFeeNotice')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Fixed Bottom Section */}
          <div className="w-[480px] px-6 py-4 absolute bottom-0 left-0 bg-white border-t border-[#dddddd] flex flex-col justify-center items-center gap-4">
            {/* Preview Section */}
            <div className="w-full flex flex-col justify-start items-start gap-1">
              <div className="w-[432px] inline-flex justify-between items-center">
                <div className="text-[#111111] text-sm">
                  {t('withdraw.totalWithdraw')}
                </div>
                <div className="text-right">
                  {isLoadingPreview ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                  ) : (
                    <>
                      <span className="text-black text-sm">¥</span>
                      <span className="text-black text-sm">
                        {' ' + (withdrawableAmount || 0).toLocaleString()}
                      </span>
                    </>
                  )}
                </div>
              </div>
              
              {/* Preview Data - Always show when we have initial data or loading */}
              <div className="w-[432px] inline-flex justify-between items-center">
                <div className="text-[#666666] text-sm">
                  {t('withdraw.withdrawalFee')}
                </div>
                <div className="text-right">
                  {isLoadingPreview ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                  ) : (
                    <>
                      <span className="text-black text-sm font-medium">-¥</span>
                      <span className="text-black text-sm font-medium">
                        {(previewData?.fee || 0).toLocaleString()}
                      </span>
                    </>
                  )}
                </div>
              </div>
              
              <div className="w-[432px] inline-flex justify-between items-center">
                <div className="text-[#111111] text-sm font-bold">
                  {t('withdraw.finalAmount')}
                </div>
                <div className="text-right">
                  {isLoadingPreview ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                  ) : (
                    <>
                      <span className="text-black text-sm font-bold">¥</span>
                      <span className="text-black text-lg font-bold">
                        {' ' + (previewData?.payout || 0).toLocaleString()}
                      </span>
                    </>
                  )}
                </div>
              </div>

            </div>
            <Button
              onClick={handleSubmit}
              variant="primary"
              size="xl"
              disabled={!isFormValid || isSubmitting || !canWithdraw || isLoadingPreview}
              className="w-full"
            >
              {isSubmitting ? t('common.loading') : t('withdraw.submitButton')}
            </Button>
          </div>
        </div>
      </div>

      <WithdrawDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={onClose}
        withdrawalData={withdrawalResult}
        disableBg
      />
    </>
  );
};

export default WithdrawModal;
